import { ref } from 'vue';
import { useRafFn } from '@vueuse/core';
import type { ZoneAnimation } from '@/types';

export function useAnimatedElementsSingleton() {
  const FRAMES_PER_SECOND = 30;
  const FRAMES_PER_LOOP = 360;

  const animations = ref<ZoneAnimation[]>([
    [
      {
        name: 'anim-id-0-A',
        alt: 'train',
        width: 1399,
        height: 435,
        left: 2331,
        top: 289,
        startingFrame: 120,
        endingFrame: 239,
        frames: 120,
        duration: 10,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-1-A',
        alt: 'boat',
        width: 1256,
        height: 518,
        left: 3243,
        top: 2481,
        startingFrame: 96,
        endingFrame: 275,
        frames: 180,
        duration: 15,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-2-A',
        alt: 'crane A',
        width: 821,
        height: 579,
        left: 2221,
        top: 1045,
        startingFrame: 60,
        endingFrame: 143,
        frames: 84,
        duration: 7,
        engine: 'webgl',
      },
      {
        name: 'anim-id-2-B',
        alt: 'crane B',
        width: 821,
        height: 579,
        left: 2221,
        top: 1045,
        startingFrame: 228,
        endingFrame: 311,
        frames: 84,
        duration: 7,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-3-A',
        alt: 'quarry vehicle 1 A',
        width: 174,
        height: 99,
        left: 1352,
        top: 610,
        startingFrame: 72,
        endingFrame: 95,
        frames: 24,
        duration: 2,
        engine: 'sprite',
      },
      {
        name: 'anim-id-3-B',
        alt: 'quarry vehicle 1 B',
        width: 174,
        height: 99,
        left: 1352,
        top: 610,
        startingFrame: 120,
        endingFrame: 143,
        frames: 24,
        duration: 2,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-4-A',
        alt: 'crane (port)',
        width: 721,
        height: 433,
        left: 2947,
        top: 2238,
        startingFrame: 72,
        endingFrame: 179,
        frames: 108,
        duration: 9,
        engine: 'webgl',
      },
      {
        name: 'anim-id-4-B',
        alt: 'crane (port)',
        width: 721,
        height: 433,
        left: 2947,
        top: 2238,
        startingFrame: 252,
        endingFrame: 347,
        frames: 96,
        duration: 8,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-5-A',
        alt: 'eolienne moderne',
        width: 539,
        height: 397,
        left: 0,
        top: 811,
        startingFrame: 1,
        endingFrame: 120,
        frames: 120,
        duration: 10,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-6-A',
        alt: 'moulin vent agricole',
        width: 239,
        height: 149,
        left: 528,
        top: 1991,
        startingFrame: 1,
        endingFrame: 120,
        frames: 120,
        duration: 10,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-7-A',
        alt: 'red car A',
        width: 294,
        height: 326,
        left: 746,
        top: 1381,
        startingFrame: 1,
        endingFrame: 84,
        frames: 84,
        duration: 7,
        engine: 'webgl',
      },
      {
        name: 'anim-id-7-B',
        alt: 'red car B',
        width: 294,
        height: 326,
        left: 746,
        top: 1381,
        startingFrame: 240,
        endingFrame: 347,
        frames: 108,
        duration: 9,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-8-A',
        alt: 'black car A',
        width: 870,
        height: 336,
        left: 1607,
        top: 1422,
        startingFrame: 24,
        endingFrame: 143,
        frames: 120,
        duration: 10,
        engine: 'webgl',
      },
      {
        name: 'anim-id-8-B',
        alt: 'black car B',
        width: 870,
        height: 336,
        left: 1607,
        top: 1422,
        startingFrame: 240,
        endingFrame: 347,
        frames: 108,
        duration: 9,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-9-A',
        alt: 'brown car A',
        width: 836,
        height: 398,
        left: 2678,
        top: 1858,
        startingFrame: 60,
        endingFrame: 179,
        frames: 120,
        duration: 10,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-10-A',
        alt: 'green car A',
        width: 765,
        height: 327,
        left: 2421,
        top: 1764,
        startingFrame: 12,
        endingFrame: 143,
        frames: 132,
        duration: 11,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-11-A',
        alt: 'yellow car A',
        width: 516,
        height: 417,
        left: 3983,
        top: 1371,
        startingFrame: 84,
        endingFrame: 191,
        frames: 108,
        duration: 9,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-12-A',
        alt: 'black car A',
        width: 929,
        height: 324,
        left: 3570,
        top: 1509,
        startingFrame: 113,
        endingFrame: 208,
        frames: 96,
        duration: 8,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-13-A',
        alt: 'green car A',
        width: 74,
        height: 96,
        left: 2710,
        top: 1070,
        startingFrame: 132,
        endingFrame: 155,
        frames: 24,
        duration: 2,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-14-A',
        alt: 'blue car A',
        width: 249,
        height: 326,
        left: 3104,
        top: 1107,
        startingFrame: 12,
        endingFrame: 83,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-15-A',
        alt: 'yellow car A',
        width: 435,
        height: 215,
        left: 3594,
        top: 1416,
        startingFrame: 264,
        endingFrame: 347,
        frames: 84,
        duration: 7,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-16-A',
        alt: 'brown car A',
        width: 314,
        height: 98,
        left: 3906,
        top: 1098,
        startingFrame: 288,
        endingFrame: 359,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-17-A',
        alt: 'yellow car A',
        width: 174,
        height: 182,
        left: 3354,
        top: 777,
        startingFrame: 240,
        endingFrame: 311,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-18-A',
        alt: 'black car A',
        width: 365,
        height: 178,
        left: 3782,
        top: 512,
        startingFrame: 264,
        endingFrame: 323,
        frames: 60,
        duration: 5,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-19-A',
        alt: 'blue car A',
        width: 250,
        height: 123,
        left: 3923,
        top: 827,
        startingFrame: 216,
        endingFrame: 275,
        frames: 60,
        duration: 5,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-20-A',
        alt: 'orange car A',
        width: 597,
        height: 242,
        left: 2332,
        top: 1324,
        startingFrame: 240,
        endingFrame: 323,
        frames: 84,
        duration: 7,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-21-A',
        alt: 'white car A',
        width: 482,
        height: 377,
        left: 4017,
        top: 1403,
        startingFrame: 36,
        endingFrame: 143,
        frames: 108,
        duration: 9,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-22-A',
        alt: 'cream car A',
        width: 354,
        height: 202,
        left: 3842,
        top: 1626,
        startingFrame: 144,
        endingFrame: 227,
        frames: 84,
        duration: 7,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-23-A',
        alt: 'blue car A',
        width: 181,
        height: 210,
        left: 3348,
        top: 761,
        startingFrame: 132,
        endingFrame: 203,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-24-A',
        alt: 'orange car A',
        width: 586,
        height: 275,
        left: 3149,
        top: 1646,
        startingFrame: 288,
        endingFrame: 335,
        frames: 48,
        duration: 4,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-25-A',
        alt: 'cream car A',
        width: 436,
        height: 183,
        left: 3735,
        top: 802,
        startingFrame: 48,
        endingFrame: 131,
        frames: 84,
        duration: 7,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-26-A',
        alt: 'blue car A',
        width: 607,
        height: 236,
        left: 2326,
        top: 1340,
        startingFrame: 240,
        endingFrame: 311,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-27-A',
        alt: 'green car A',
        width: 721,
        height: 355,
        left: 3778,
        top: 331,
        startingFrame: 60,
        endingFrame: 167,
        frames: 108,
        duration: 9,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-28-A',
        alt: 'white car A',
        width: 744,
        height: 301,
        left: 3247,
        top: 2130,
        startingFrame: 36,
        endingFrame: 143,
        frames: 108,
        duration: 9,
        engine: 'webgl',
      },
      {
        name: 'anim-id-28-B',
        alt: 'white car B',
        width: 744,
        height: 301,
        left: 3247,
        top: 2130,
        startingFrame: 216,
        endingFrame: 311,
        frames: 96,
        duration: 8,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-29-A',
        alt: 'white Car A',
        width: 458,
        height: 244,
        left: 3136,
        top: 2263,
        startingFrame: 108,
        endingFrame: 227,
        frames: 120,
        duration: 10,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-30-A',
        alt: 'white car A',
        width: 313,
        height: 164,
        left: 1382,
        top: 2055,
        startingFrame: 132,
        endingFrame: 191,
        frames: 60,
        duration: 5,
        engine: 'sprite',
      },
      {
        name: 'anim-id-30-B',
        alt: 'white car B',
        width: 313,
        height: 164,
        left: 1382,
        top: 2055,
        startingFrame: 276,
        endingFrame: 299,
        frames: 24,
        duration: 2,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-31-A',
        alt: 'blue car A',
        width: 274,
        height: 165,
        left: 1376,
        top: 2074,
        startingFrame: 12,
        endingFrame: 83,
        frames: 72,
        duration: 6,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-32-A',
        alt: 'green car A',
        width: 412,
        height: 196,
        left: 85,
        top: 1990,
        startingFrame: 12,
        endingFrame: 167,
        frames: 156,
        duration: 13,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-33-A',
        alt: 'red car A',
        width: 270,
        height: 99,
        left: 463,
        top: 573,
        startingFrame: 60,
        endingFrame: 107,
        frames: 48,
        duration: 4,
        engine: 'webgl',
      },
      {
        name: 'anim-id-33-B',
        alt: 'red car A',
        width: 270,
        height: 99,
        left: 463,
        top: 573,
        startingFrame: 204,
        endingFrame: 251,
        frames: 48,
        duration: 4,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-34-A',
        alt: 'yellow car A',
        width: 333,
        height: 174,
        left: 178,
        top: 463,
        startingFrame: 84,
        endingFrame: 167,
        frames: 84,
        duration: 7,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-35-A',
        alt: 'yellow car A',
        width: 866,
        height: 495,
        left: 1125,
        top: 324,
        startingFrame: 84,
        endingFrame: 203,
        frames: 120,
        duration: 10,
        engine: 'webgl',
      },
    ],
    [
      {
        name: 'anim-id-36-A',
        alt: 'white car A',
        width: 522,
        height: 195,
        left: 1985,
        top: 544,
        startingFrame: 96,
        endingFrame: 191,
        frames: 96,
        duration: 8,
        engine: 'sprite',
      },
      {
        name: 'anim-id-36-B',
        alt: 'white car A',
        width: 522,
        height: 195,
        left: 1985,
        top: 544,
        startingFrame: 240,
        endingFrame: 347,
        frames: 108,
        duration: 9,
        engine: 'sprite',
      },
    ],
    [
      {
        name: 'anim-id-37-A',
        alt: 'red car A',
        width: 465,
        height: 511,
        left: 1786,
        top: 450,
        startingFrame: 1,
        endingFrame: 144,
        frames: 144,
        duration: 12,
        engine: 'webgl',
      },
      {
        name: 'anim-id-37-B',
        alt: 'red car B',
        width: 465,
        height: 511,
        left: 1786,
        top: 450,
        startingFrame: 204,
        endingFrame: 347,
        frames: 144,
        duration: 12,
        engine: 'webgl',
      },
    ],
  ]);

  const frame = ref(0);
  const { pause, resume, isActive } = useRafFn(
    () => {
      frame.value = (frame.value + 1) % FRAMES_PER_LOOP;
    },
    {
      immediate: true,
      fpsLimit: FRAMES_PER_SECOND,
    },
  );

  const pauseUntilFinished = async (callback: () => unknown) => {
    pause();
    await callback();
    resume();
  };

  return {
    animations,
    frame,
    pause,
    pauseUntilFinished,
    resume,
    isActive,
  };
}

const cached = useAnimatedElementsSingleton();
export const useAnimatedElements = () => cached;
